import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { TaxProjection } from "./useTax";

/**
 * Strategy assumptions = ONLY user-supplied planning inputs. Numeric fields are
 * nullable on purpose — when they're missing the planner blocks rather than
 * substituting a guess. The old marginal-rate / sweep-% sliders have been
 * deleted; those are now COMPUTED outputs of the tax engine.
 */
export interface StrategyAssumptions {
  id: string;
  ownerUserId: string;
  hourlyRate: number | null;
  hoursPerWeek: number | null;
  /**
   * ISO yyyy-MM-dd string (DateOnly serialized). When the contract starts billing.
   * Required input — the planner derives "billable weeks this year" from this and `contractEndDate`.
   */
  contractStartDate: string | null;
  /**
   * Optional. `null` means open-ended → assume the contract continues through Dec 31 of the projection year.
   */
  contractEndDate: string | null;
  projectedAnnualOverhead: number | null;
  megaBackdoorRothAvailable: boolean;
  targetSolo401kAnnual: number | null;
  hsaEligible: boolean;
  notes: string;
}

export interface CashAllocationMissingInput {
  fieldKey: string;
  label: string;
  routeHint: string | null;
  why: string;
  isBlocking: boolean;
}

export interface CashAllocationPlan {
  isBlocked: boolean;
  projectedAnnualRevenue: number | null;
  projectedNetSe: number | null;
  marginalTotalRate: number | null;
  taxSavingsSweepPercent: number | null;
  retirementHoldingPercent: number | null;
  operatingBufferPercent: number | null;
  ownerDrawPercent: number | null;
  projectedAnnualSolo401k: number | null;
  projectedAnnualTaxReserve: number | null;
  /** Calendar weeks the contract bills inside the projection year (derived from start/end dates). */
  effectiveBillableWeeks: number | null;
  revenueSource: "Blocked" | "PlanFromRateAndHours" | "ActualizedYtd" | "ProjectedAnnualOverride";
  actualRevenueYtd: number | null;
  actualRevenueAsOfDate: string | null;
  blockingReason: string | null;
  missingInputs: CashAllocationMissingInput[];
}

/**
 * Derived view of YTD invoice + transaction activity. Source of truth for
 * "is the user's app reacting to real money?" — when `isEmpty` is true the
 * planner is running on rate × hours guesses, otherwise it's running on
 * actuals.
 */
export interface BusinessRollup {
  taxYear: number;
  asOfDate: string;
  dayOfYear: number;
  daysInYear: number;
  revenueYtd: number;
  expenseYtd: number;
  taxYtd: number;
  retirementYtd: number;
  ownerDrawYtd: number;
  outstandingInvoiced: number;
  paidInvoiceCount: number;
  trailing90DayRevenue: number;
  projectedAnnualFrom90DayRunRate: number;
  projectedAnnualFromYtdAnnualization: number;
  isEmpty: boolean;
}

/** Server response wraps the plan + projection + rollup so the UI can show all three. */
export interface CashAllocationResponse {
  plan: CashAllocationPlan;
  projection: TaxProjection;
  rollup: BusinessRollup;
}

export type EntityRecommendation =
  | "Default"
  | "Year2"
  | "Year2Plus"
  | "OnlyForRetainedCapital"
  | "NotRecommended";

export interface ReasonableSalaryRangeInfo {
  lowAuditRisk: number;
  defensible: number;
  aggressiveAuditRisk: number;
  citation: string;
  source: string;
}

export type ReasonableSalaryStrategy = "Low" | "Defensible" | "Aggressive";

export interface EntityScenarioResult {
  structure: string;
  projectedTotalTax: number;
  projectedAdminCost: number;
  projectedTotalCost: number;
  seOrFicaTax: number;
  qbiDeduction: number;
  summary: string;
  recommendation: EntityRecommendation;
  requiredActions: string[];
  isBlocked: boolean;
  blockingReason: string | null;
  /** BLS-anchored reasonable salary range (S-Corp scenarios only). */
  reasonableSalaryRange?: ReasonableSalaryRangeInfo | null;
  targetReasonableSalary?: number | null;
  /** Annual payroll cost (Gusto + WI UI + FUTA + WC) folded into projectedAdminCost. */
  payrollCostAnnual?: number | null;
  /** Federal benefit of WI Pass-Through Entity election (escapes $10k SALT cap). */
  wisconsinPteFederalSavings?: number | null;
  /** Annual DB plan contribution modeled (DB scenario only). */
  definedBenefitContribution?: number | null;
}

export interface EntityComparisonOptions {
  childrenOnPayroll?: number;
  wagePerChild?: number;
  /** Apply Wisconsin Pass-Through Entity election to S-Corp scenarios. */
  wiPte?: boolean;
  /** Add the S-Corp + Defined Benefit Plan column. */
  includeDb?: boolean;
  /** Override DB contribution; default is engine estimate ~30% of distributable. */
  dbContribution?: number;
  salaryStrategy?: ReasonableSalaryStrategy;
}

const ASSUMPTIONS_KEY = ["strategy", "assumptions"];

export function useStrategyAssumptions() {
  return useQuery({
    queryKey: ASSUMPTIONS_KEY,
    queryFn: async (): Promise<StrategyAssumptions> => {
      const r = await api().get<StrategyAssumptions>("/api/strategy/assumptions");
      return r.data;
    },
  });
}

export function useUpdateStrategyAssumptions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<StrategyAssumptions>) => {
      const r = await api().put<StrategyAssumptions>("/api/strategy/assumptions", input);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ASSUMPTIONS_KEY });
      qc.invalidateQueries({ queryKey: ["strategy", "cash-allocation"] });
      qc.invalidateQueries({ queryKey: ["strategy", "entity-comparison"] });
      qc.invalidateQueries({ queryKey: ["tax", "projection"] });
      qc.invalidateQueries({ queryKey: ["advisor", "cards"] });
    },
  });
}

export function useCashAllocation() {
  return useQuery({
    queryKey: ["strategy", "cash-allocation"],
    queryFn: async (): Promise<CashAllocationResponse> => {
      const r = await api().get<CashAllocationResponse>("/api/strategy/cash-allocation");
      return r.data;
    },
  });
}

export function useEntityComparison(params: EntityComparisonOptions = {}) {
  return useQuery({
    queryKey: ["strategy", "entity-comparison", params],
    queryFn: async (): Promise<EntityScenarioResult[]> => {
      const r = await api().get<EntityScenarioResult[]>("/api/strategy/entity-comparison", {
        params,
      });
      return r.data;
    },
  });
}

// =============================================================================
// Phase E: Sankey graph data (money / tax / what-if S-Corp)
// =============================================================================
export type SankeyView = "money" | "tax" | "whatif";

export interface SankeyNodeDto {
  id: string;
  category: string;
}

export interface SankeyLinkDto {
  source: string;
  target: string;
  value: number;
}

export interface SankeyResponse {
  blocked: boolean;
  reason?: string;
  view: SankeyView;
  nodes: SankeyNodeDto[];
  links: SankeyLinkDto[];
}

export function useSankey(view: SankeyView, year?: number) {
  return useQuery({
    queryKey: ["strategy", "sankey", view, year ?? "current"],
    queryFn: async (): Promise<SankeyResponse> => {
      const r = await api().get<SankeyResponse>("/api/strategy/sankey", {
        params: { view, ...(year ? { year } : {}) },
      });
      return r.data;
    },
    staleTime: 30_000,
  });
}
